import React, { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Calendar, Eye, UserCheck } from "lucide-react";
import { Button } from "@shared/ui/button";
import { ConfirmUpdateModal } from "./ConfirmUpdateModal";
import { SelectConsumerModal, CandidateConsumer } from "./SelectConsumerModal";
import { useUnitWiseConsumers } from "@features/onboarding/utility-setup/hooks";
import { onboardingApi } from "@features/onboarding/utility-setup/api";
import { downloadFileByUrl } from "@shared/utils/utils";
import { toast } from "@shared/hooks/use-toast";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { PAGE_SIZE } from "@shared/utils/constants";
import { PageHeader } from "@shared/components/PageHeader";

interface ConsumerRecord {
  id: string;
  accountNo: string;
  name: string;
  email: string;
  phone: string;
  plan: string;
  services: string[];
  outstanding: number | string;
  connectionDate: string;
  disconnectionDate: string;
}

const getServiceBadgeStyle = (service: string) => {
  switch (service) {
    case "Water":
      return "bg-blue-50 text-blue-600 border-blue-200/80";
    case "Electricity":
      return "bg-amber-50 text-amber-700 border-amber-200/80";
    case "Gas":
      return "bg-orange-50 text-orange-700 border-orange-200/80";
    case "EV":
      return "bg-sky-50 text-sky-600 border-sky-200/80";
    case "Broadband":
      return "bg-purple-50 text-purple-700 border-purple-200/80";
    case "Hot Water":
      return "bg-rose-50 text-rose-600 border-rose-200/80";
    case "Waste Water":
      return "bg-emerald-50 text-emerald-700 border-emerald-200/80";
    case "Solar":
      return "bg-teal-50 text-teal-700 border-teal-200/80";
    default:
      return "bg-gray-50 text-gray-700 border-gray-200";
  }
};

export const Consumers = () => {
  const navigate = useNavigate();
  const { code } = useParams<{ code: string }>();
  const decodedUnitCode = useMemo(() => decodeURIComponent(code || ""), [code]);
  const remoteUtilityId = useRemoteUtilityId();
  const currency = useCurrency();

  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);

  const { data: consumersListResponse, isLoading } = useUnitWiseConsumers({
    unitCode: decodedUnitCode,
    remoteUtilityId,
    searchData: searchQuery,
    page,
    limit: PAGE_SIZE,
  });

  const consumersData: ConsumerRecord[] = useMemo(() => {
    if (!consumersListResponse) return [];

    const rawList = Array.isArray(consumersListResponse.results)
      ? consumersListResponse.results
      : [];

    return rawList.map((item: any, idx: number) => {
      const services = Array.isArray(item.utilityServices)
        ? item.utilityServices
        : [];

      const rawDisconnection = item.disconnectionDate;
      const isCurrent = item.isCurrent;

      return {
        id: item.consumerId,
        accountNo: item.accountNo || "N/A",
        name: item.name || "N/A",
        email: item.email || "N/A",
        phone: item.phone || "N/A",
        plan: item.planName || "N/A",
        services: services.length > 0 ? services : [],
        outstanding: item.outstanding ?? 0,
        connectionDate: item.connectionDate || "N/A",
        disconnectionDate: rawDisconnection
          ? rawDisconnection
          : isCurrent
            ? "Active"
            : "N/A",
      };
    });
  }, [consumersListResponse]);

  const totalItems = consumersListResponse?.count;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE) || 1;

  const pagination = useMemo(
    () => ({
      currentPage: page,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? (page - 1) * PAGE_SIZE + 1 : 0,
      endItem: Math.min(page * PAGE_SIZE, totalItems),
    }),
    [page, totalPages, totalItems],
  );

  const activeCount = useMemo(
    () => consumersData.filter((c) => c.disconnectionDate === "Active").length,
    [consumersData],
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSearch = (searchTerm: string) => {
    setSearchQuery(searchTerm);
    setPage(1);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await onboardingApi.getUnitWiseConsumers(
        decodedUnitCode,
        remoteUtilityId,
        undefined,
        searchQuery,
        page,
        PAGE_SIZE,
        true,
      );

      const fileUrl =
        response?.file_url ||
        response?.file ||
        response?.result?.file_url ||
        response?.result?.url ||
        response?.url ||
        response?.link ||
        response?.data?.file_url ||
        response?.data?.url;

      if (fileUrl) {
        downloadFileByUrl(fileUrl);
        toast({
          title: "Export Successful",
          description: "Consumer list exported successfully.",
        });
      } else if (response?.message) {
        toast({
          title: "Export Initiated",
          description: response.message,
        });
      } else {
        toast({
          title: "Export Successful",
          description: "Consumer list exported.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Export Failed",
        description: error?.message || "Failed to export consumer list.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleAttachConsumer = () => {
    setIsSelectModalOpen(false);
  };

  const columns: TableColumn<ConsumerRecord>[] = [
    {
      key: "accountNo",
      header: "Account No.",
    },
    {
      key: "name",
      header: "Name",
    },
    {
      key: "email",
      header: "Email",
    },
    {
      key: "phone",
      header: "Phone",
    },
    {
      key: "plan",
      header: "Plan",
    },
    {
      key: "services",
      header: "Utility Services",
      render: (servicesArr: string[]) => (
        <div className="flex flex-wrap gap-1 max-w-[210px]">
          {servicesArr && servicesArr.length > 0 ? (
            servicesArr.map((service, i) => (
              <span
                key={i}
                className={`text-[11px] font-medium px-2 py-0.5 rounded border ${getServiceBadgeStyle(
                  service,
                )}`}
              >
                {service}
              </span>
            ))
          ) : (
            <span className="text-xs text-gray-400">N/A</span>
          )}
        </div>
      ),
    },
    {
      key: "outstanding",
      header: "Outstanding",
      render: (val: number | string) => {
        const numVal = typeof val === "number" ? val : parseFloat(val) || 0;
        const isZero = numVal === 0;
        return (
          <span
            className={`text-xs font-bold ${
              isZero ? "text-emerald-600" : "text-red-600"
            }`}
          >
            {currency} {numVal.toFixed(2)}
          </span>
        );
      },
    },
    {
      key: "connectionDate",
      header: "Connection Date",
      render: (val) => (
        <span className="text-xs text-gray-500 flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          {val}
        </span>
      ),
    },
    {
      key: "disconnectionDate",
      header: "Disconnection Date",
      render: (val: string) => {
        if (val === "Active") {
          return (
            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 inline-block">
              Active
            </span>
          );
        }
        return (
          <span className="text-xs text-gray-500 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            {val}
          </span>
        );
      },
    },
  ];
  const action = [
    {
      label: "View",
      icon: <Eye className="w-3.5 h-3.5 mr-1 text-gray-500" />,
      onClick: (_row: any) => {
        navigate(`/cx/consumer-management/${_row.accountNo}/`);
      },
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header Info */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <PageHeader
          title="Consumers"
          subtitle="Manage consumer assignments and view connection details."
        />
        <Button
          variant="default"
          size="sm"
          onClick={() => setIsConfirmModalOpen(true)}
        >
          <UserCheck className="w-4 h-4" />
          Update Consumer
        </Button>
      </div>

      <div className="text-sm font-semibold text-gray-700">
        {totalItems} consumers ·{" "}
        <span className="text-emerald-600 font-bold">{activeCount} active</span>
      </div>

      {/* Generic Table */}
      <GenericTable
        data={consumersData}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        onSearch={handleSearch}
        searchPlaceholder="Search by name, account, email..."
        pagination={pagination}
        onPageChange={handlePageChange}
        onExport={handleExport}
        exportLoading={isExporting}
        actions={action}
        actionsType="icons"
      />

      {/* Dialog 1: Confirm Replacement Warning Dialog */}
      <ConfirmUpdateModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={() => {
          setIsConfirmModalOpen(false);
          setIsSelectModalOpen(true);
        }}
      />

      {/* Dialog 2: Search & Select Consumer Dialog */}
      <SelectConsumerModal
        isOpen={isSelectModalOpen}
        onClose={() => setIsSelectModalOpen(false)}
        onBack={() => {
          setIsSelectModalOpen(false);
          setIsConfirmModalOpen(true);
        }}
        onAttachConsumer={handleAttachConsumer}
      />
    </div>
  );
};

export default Consumers;
